<template>
    <div class="lazy-component">
        <dynamic-component :name="name" #default="{ loading: componentLoading, error: componentError, component }">
            <fetch-data
                v-bind="fetchData"
                #default="{ response, loading, error, fetchData: fetchDataAgain }"
                @response="onResponse"
                @error="onError"
            >
                <div v-if="loading || componentLoading">
                    <!-- @slot Loading content -->
                    <slot name="loading">
                        <div class="icon-text">
                            <icon name="spinner" size="xs" color="primary" spin />
                            <span class="color--grey-900 ts--body">{{ $t('lazy-component.loading') }}</span>
                        </div>
                    </slot>
                </div>
                <div v-else-if="error || componentError">
                    <!-- @slot Error content -->
                    <slot name="error" v-bind="{ error, componentError }">
                        <div class="lazy-component__error">
                            <span class="color--grey-900 ts--body">{{ $t('lazy-component.error-message') }}</span>
                            <button
                                v-if="fetchData.url"
                                type="button"
                                class="btn btn--secondary btn--xxs m-left--xs icon-text"
                                @click="fetchDataAgain"
                            >
                                <icon name="generic-refresh" size="xxs" />
                                <span>{{ $t('lazy-component.try-again') }}</span>
                            </button>
                        </div>
                    </slot>
                </div>
                <div v-else>
                    <!-- @slot Default content when having `response` and `component` objects available -->
                    <slot v-bind="{ response, component }"></slot>
                </div>
            </fetch-data>
        </dynamic-component>
    </div>
</template>

<script>
import DynamicComponent from '@v4/common/components/atoms/DynamicComponent';
import FetchData from '@v4/common/components/atoms/FetchData';
import Icon from '@v4/common/components/atoms/Icon';

/**
 * Combines together `InView`, `DynamicComponent` and `FetchData` components
 * and handles by default loading/error cases
 */
export default {
    name: 'LazyComponent',
    components: {
        DynamicComponent,
        FetchData,
        Icon,
    },
    props: {
        /**
         * Component name which should be located in `common/components/dynamic` folder
         */
        name: {
            type: String,
            default: '',
        },
        /**
         * Props passed to `FetchData` component
         */
        fetchData: {
            type: Object,
            default: () => ({}),
        },
    },
    methods: {
        onResponse(...args) {
            /**
             * Fired when getting success response from `FetchData` component
             *
             * @event response
             * @type {object}
             * @property {object} payload - Payload data
             */
            this.$emit('response', ...args);
        },
        onError(...args) {
            /**
             * Fired when getting error response from `FetchData` component
             *
             * @event response
             * @type {object}
             * @property {object} payload - Payload data
             */
            this.$emit('error', ...args);
        },
    },
};
</script>

<style lang="scss" src="@v4/common/components/atoms/LazyComponent/_index.scss"></style>
